import { useEffect, useMemo, useState } from "react";
import api from "../api";

export default function EmployeeReviews({ user }) {
  const [reviews, setReviews] = useState([]);
  const [overallReply, setOverallReply] = useState({});
  const [openMonth, setOpenMonth] = useState(null);

  /* ================= LOAD REVIEWS ================= */
  useEffect(() => {
    if (!user?._id) return;

    api
      .get(`/employee-review/my/${user._id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then((res) => setReviews(res.data || []))
      .catch(() => console.error("Failed to load reviews"));
  }, [user?._id]);

  /* ================= SUMMARY ================= */
  const summary = useMemo(() => {
    let positive = 0,
      improvement = 0,
      achieved = 0,
      pending = 0;

    reviews.forEach((r) => {
      positive += Object.keys(r.positivePoints || {}).length;
      improvement += Object.keys(r.improvementAreas || {}).length;
      r.status === "ACHIEVED" ? achieved++ : pending++;
    });

    return { positive, improvement, achieved, pending };
  }, [reviews]);

  /* ================= GROUP BY MONTH ================= */
  const groupedByMonth = useMemo(() => {
    return reviews.reduce((acc, r) => {
      const d = new Date(r.createdAt || r.assignedAt);
      const key = d.toLocaleString("default", {
        month: "long",
        year: "numeric",
      });
      acc[key] = acc[key] || [];
      acc[key].push(r);
      return acc;
    }, {});
  }, [reviews]);

  /* ================= SUBMIT REPLY (FIXED LOGIC) ================= */
  const submitReply = async (id) => {
    const existingReply = reviews.find(
      (r) => r._id === id
    )?.employeeOverallReply;

    const replyText =
      overallReply[id] !== undefined ? overallReply[id] : existingReply || "";

    if (!replyText.trim()) {
      alert("Please write reply");
      return;
    }

    try {
      await api.post(
        `/employee-review/reply/${id}`,
        {
          reply: replyText, // ✅ EXACT MATCH WITH BACKEND
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      // reload updated reviews
      const res = await api.get(`/employee-review/my/${user._id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      setReviews(res.data || []);
      setOverallReply({});
      alert("Reply submitted successfully");
    } catch (err) {
      console.error("Submit reply error:", err);
      alert("Failed to submit reply");
    }
  };

  if (!reviews.length) {
    return <div style={{ padding: 40 }}>No reviews available</div>;
  }

  return (
    <div style={pageStyle}>
      <h2 style={pageTitle}>My Performance Reviews</h2>

      {/* ================= SUMMARY ================= */}
      <div style={summaryGrid}>
        <SummaryCard
          icon="⭐"
          label="Positive"
          value={summary.positive}
          bg="#dcfce7"
          color="#16a34a"
        />
        <SummaryCard
          icon="⚠️"
          label="Improvement"
          value={summary.improvement}
          bg="#fee2e2"
          color="#dc2626"
        />
        <SummaryCard
          icon="🏆"
          label="Achieved"
          value={summary.achieved}
          bg="#fef3c7"
          color="#ca8a04"
        />
        <SummaryCard
          icon="⏳"
          label="Pending"
          value={summary.pending}
          bg="#e0e7ff"
          color="#4f46e5"
        />
      </div>

      {/* ================= MONTH ACCORDION ================= */}
      {Object.entries(groupedByMonth).map(([month, items]) => {
        const open = openMonth === month;

        return (
          <div key={month} style={accordion}>
            <div
              style={accordionHeader}
              onClick={() => setOpenMonth(open ? null : month)}
            >
              <b>{month}</b>
              <span>{open ? "▲" : "▼"}</span>
            </div>

            {open && (
              <div style={{ padding: 30 }}>
                {items.map((r) => (
                  <div key={r._id} style={reviewCard}>
                    {/* HEADER */}
                    <div style={headerRow}>
                      <span style={statusBadge(r.status)}>{r.status}</span>
                      <span style={dateText}>
                        Assigned on{" "}
                        {new Date(
                          r.createdAt || r.assignedAt
                        ).toLocaleDateString()}
                      </span>
                    </div>

                    {/* POSITIVE + IMPROVEMENT */}
                    <div style={twoColGrid}>
                      <InfoBox title="⭐ Positive Points">
                        {Object.entries(r.positivePoints || {}).map(
                          ([k, v]) => (
                            <li key={k}>
                              <b>{k}</b> – {v}
                            </li>
                          )
                        )}
                      </InfoBox>

                      <InfoBox title="⚠️ Areas of Improvement">
                        {Object.entries(r.improvementAreas || {}).map(
                          ([k, v]) => (
                            <li key={k}>
                              <b>{k}</b> – {v}
                            </li>
                          )
                        )}
                      </InfoBox>
                    </div>

                    {/* MANAGEMENT SUGGESTION */}
                    {r.managementSuggestion && (
                      <div style={managementWrapper}>
                        <h4 style={managementTitle}>
                          💡 Suggestion from Management
                        </h4>
                        <div style={managementBox}>
                          {r.managementSuggestion}
                        </div>
                      </div>
                    )}

                    {/* REPLY */}
                    <div style={replyBox}>
                      <h4>📝 Your Overall Reply</h4>
                      <textarea
                        style={{
                          ...textarea,
                          background:
                            r.status === "REPLIED" || r.status === "ACHIEVED"
                              ? "#f8fafc"
                              : "#ffffff",
                          cursor:
                            r.status === "REPLIED" || r.status === "ACHIEVED"
                              ? "not-allowed"
                              : "text",
                        }}
                        placeholder="Write your response..."
                        disabled={
                          r.status === "REPLIED" || r.status === "ACHIEVED"
                        }
                        readOnly={
                          r.status === "REPLIED" || r.status === "ACHIEVED"
                        }
                        value={
                          r.employeeOverallReply || overallReply[r._id] || ""
                        }
                        onChange={(e) => {
                          if (r.status === "REPLIED" || r.status === "ACHIEVED")
                            return;

                          setOverallReply({
                            ...overallReply,
                            [r._id]: e.target.value,
                          });
                        }}
                      />

                      {r.status !== "ACHIEVED" && (
                        <button
                          style={submitBtn}
                          onClick={() => submitReply(r._id)}
                        >
                          Submit Reply
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ================= COMPONENTS ================= */

const SummaryCard = ({ icon, label, value, bg, color }) => (
  <div style={{ ...summaryItem, background: bg }}>
    <div style={{ fontSize: 16, fontWeight: 600 }}>
      {icon} {label}
    </div>
    <div style={{ fontSize: 32, fontWeight: 800, color }}>{value}</div>
  </div>
);

const InfoBox = ({ title, children }) => (
  <div style={infoBox}>
    <h4>{title}</h4>
    <ul>{children}</ul>
  </div>
);

/* ================= STYLES ================= */

const pageStyle = {
  padding: 40,
  minHeight: "100vh",
  background: "#f8fafc",
};

const pageTitle = {
  fontSize: 26,
  fontWeight: 800,
  marginBottom: 24,
};

const summaryGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(4,1fr)",
  gap: 18,
  marginBottom: 36,
};

const summaryItem = {
  padding: "22px 26px",
  borderRadius: 16,
  boxShadow: "0 10px 22px rgba(0,0,0,0.08)",
};

const accordion = {
  background: "#fff",
  borderRadius: 20,
  marginBottom: 18,
  border: "1px solid #e5e7eb",
};

const accordionHeader = {
  padding: "20px 26px",
  cursor: "pointer",
  display: "flex",
  justifyContent: "space-between",
  fontSize: 18,
};

const reviewCard = {
  background: "#ffffff",
  borderRadius: 22,
  padding: 30,
  marginBottom: 28,
  boxShadow: "0 18px 36px rgba(0,0,0,0.1)",
};

const headerRow = {
  display: "flex",
  justifyContent: "space-between",
  marginBottom: 22,
};

const statusBadge = (status) => ({
  padding: "6px 14px",
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 700,
  background: status === "ACHIEVED" ? "#dcfce7" : "#fef3c7",
});

const dateText = {
  fontSize: 14,
  color: "#64748b",
};

const twoColGrid = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 24,
};

const infoBox = {
  background: "#f8fafc",
  padding: 20,
  borderRadius: 16,
  border: "1px solid #e5e7eb",
};

const managementWrapper = {
  marginTop: 28,
};

const managementTitle = {
  fontSize: 16,
  fontWeight: 700,
  marginBottom: 10,
  color: "#1e3a8a",
};

const managementBox = {
  padding: "18px 24px",
  borderRadius: 16,
  background: "#eef2ff",
  borderLeft: "5px solid #4f46e5",
  fontSize: 15,
  lineHeight: 1.8,
};

const replyBox = {
  marginTop: 28,
  paddingTop: 22,
  borderTop: "1px dashed #e5e7eb",
};

const textarea = {
  width: "100%",
  minHeight: 110,
  padding: 16,
  borderRadius: 14,
  border: "1px solid #e5e7eb",
};

const submitBtn = {
  marginTop: 14,
  background: "#2563eb",
  color: "#fff",
  border: "none",
  padding: "12px 22px",
  borderRadius: 14,
  fontWeight: 700,
  cursor: "pointer",
};
